import { Box, Button, Card, Collapse, Icon, List, Text } from '@ksyos/design-system';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import type { IProcessAction } from '../../../../common/interfaces/IProcessAction';
import type { IReduxState } from '../../../configureStore';
import { fetchJson } from '../../../store/fetch';
import { useReloadPatientAvailableActions } from '../../../utils/hooks/useReloadPatientAvailableActions';
import { Container } from '../../elements/molecules/container/Container';
import { PageSection } from '../../elements/molecules/pageSection/PageSection';

export function OsasOptInFlowChoice({
    osasPatientWantsToBeCalledAction,
    osasStartFlowAction,
    setPatientClickedContinue,
}: Readonly<{
    osasPatientWantsToBeCalledAction: IProcessAction;
    osasStartFlowAction: IProcessAction;
    setPatientClickedContinue: (value: boolean) => void;
}>) {
    const reloadPatientAvailableActions = useReloadPatientAvailableActions();
    const patientActiveInsurance = useSelector((state: IReduxState) => state.authentication.insurance);
    const [isMoreInfoOpen, setIsMoreInfoOpen] = useState(false);

    return (
        <Container isFluidOnMobile>
            <PageSection>
                <div style={{ paddingTop: '3rem' }}>
                    <Card>
                        <Box
                            display="flex"
                            justifyContent="center"
                            mt={4}
                        >
                            <img
                                alt="betalen"
                                src="/assets/images/somnology/insurance/insurance-modal-betalen.svg"
                                style={{ height: '120px' }}
                            />
                        </Box>
                        <Card.Header title="Hoe betaal je alleen je eigen risico? " />
                        <Card.Body>
                            <Box maxWidth={620}>
                                <Text
                                    mb={3}
                                    size="md"
                                >
                                    Ksyos heeft geen contract met{' '}
                                    {patientActiveInsurance?.uzovi?.name
                                        ? `${patientActiveInsurance?.uzovi?.name}, `
                                        : ''}
                                    jouw zorgverzekeraar. Daarom krijg je later een factuur. Je regelt zelf de betaling
                                    daarvan.
                                    <Text
                                        as="span"
                                        fontWeight="bold"
                                        size="md"
                                    >
                                        {' '}
                                        Als je alle stappen volgt, betaal je alleen je eigen risico.
                                    </Text>
                                </Text>

                                <div id="reimbursement-steps">
                                    <Collapse isOpen={isMoreInfoOpen}>
                                        <Text mb={3}>De stappen nadat je de factuur hebt ontvangen:</Text>
                                        <List
                                            style={{ marginBottom: '1.5rem' }}
                                            styleType="counter"
                                        >
                                            <List.Item>Stuur je factuur naar je zorgverzekeraar</List.Item>
                                            <List.Item>
                                                Stuur het vergoedingsoverzicht van je verzekeraar naar Ksyos
                                            </List.Item>
                                            <List.Item>
                                                Betaal de vergoeding van je zorgverzekeraar en je openstaande eigen
                                                risico aan Ksyos.
                                            </List.Item>
                                        </List>
                                        <Text mb={3}>
                                            {' '}
                                            Deze stappen staan later ook op de factuur specifiek uitgelegd.{' '}
                                        </Text>
                                    </Collapse>
                                </div>
                                <button
                                    aria-controls="reimbursement-steps"
                                    aria-expanded={isMoreInfoOpen}
                                    onClick={() => setIsMoreInfoOpen(!isMoreInfoOpen)}
                                    style={{
                                        alignItems: 'center',
                                        background: 'transparent',
                                        border: 0,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        marginBottom: '1rem',
                                        padding: 0,
                                    }}
                                    type="button"
                                >
                                    <Icon icon={isMoreInfoOpen ? 'caret-up' : 'caret-down'} />
                                    <Text
                                        ml={2}
                                        size="sm"
                                    >
                                        {isMoreInfoOpen
                                            ? 'Minder informatie over de stappen'
                                            : 'Meer informatie over de stappen'}
                                    </Text>
                                </button>

                                <Text size="md">
                                    Maak een keuze over hoe jij verder wilt gaan om jouw slaapzorg te starten.
                                </Text>
                            </Box>
                        </Card.Body>
                        <Card.Footer hasBorderTop={false}>
                            <Box
                                display="flex"
                                justifyContent="end"
                                mt={6}
                            >
                                <Button
                                    onClick={async () => {
                                        await fetchJson(
                                            `/api/patient/processes/${osasPatientWantsToBeCalledAction.processId}`,
                                            'POST',
                                            {
                                                actionId: osasPatientWantsToBeCalledAction.id,
                                                serviceWorkflowStepId:
                                                    osasPatientWantsToBeCalledAction.serviceWorkflowStep.id,
                                            }
                                        );
                                        await reloadPatientAvailableActions();
                                    }}
                                    variant="outline"
                                >
                                    Bel mij voor meer informatie
                                </Button>
                                <Button
                                    onClick={async () => {
                                        await fetchJson(
                                            `/api/patient/processes/${osasStartFlowAction.processId}`,
                                            'POST',
                                            {
                                                actionId: osasStartFlowAction.id,
                                                serviceWorkflowStepId: osasStartFlowAction.serviceWorkflowStep.id,
                                            }
                                        );
                                        setPatientClickedContinue(true);
                                    }}
                                    style={{ marginLeft: '1rem' }}
                                >
                                    Start mijn slaapzorg
                                </Button>
                            </Box>
                        </Card.Footer>
                    </Card>
                </div>
            </PageSection>
        </Container>
    );
}
